import {
  correctRollForwardTable,
  initializeRollForwardInfo,
} from "@/apis/forward/route";
import { useWorkbookId } from "@/hooks/use-workbook";
import queries from "@/queries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

/** src 워크북 지정 + AI 자동 매칭으로 표별 이월 짝 초기화 (Step 1 → Step 2). */
export const useInitializeRollForwardInfo = () => {
  const { data: workbookId } = useWorkbookId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["roll-forward-info-init", workbookId],
    mutationFn: async (sourceWorkbookId: string) => {
      if (!workbookId) throw new Error("Workbook ID is not found");
      return initializeRollForwardInfo(workbookId, sourceWorkbookId);
    },
    onSuccess: () => {
      if (!workbookId) return;
      queryClient.invalidateQueries(
        queries.workbook(workbookId)._ctx.rollForwardInfo(),
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "매칭 초기화에 실패했습니다.",
      );
    },
  });
};

/** 표별 매칭 교정 — 고른 src 표로 target 표의 이월 짝을 바꾼다. */
export const useCorrectRollForwardTable = () => {
  const { data: workbookId } = useWorkbookId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["roll-forward-correct", workbookId],
    mutationFn: async ({
      tableId,
      sourceTableId,
    }: {
      tableId: string;
      sourceTableId: string;
    }) => {
      if (!workbookId) throw new Error("Workbook ID is not found");
      return correctRollForwardTable(workbookId, tableId, sourceTableId);
    },
    onSuccess: () => {
      if (!workbookId) return;
      queryClient.invalidateQueries(
        queries.workbook(workbookId)._ctx.rollForwardInfo(),
      );
      toast.success("매칭이 변경되었습니다.");
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "매칭 변경에 실패했습니다.",
      );
    },
  });
};

/** 확정된 표별 이월 짝 조회 (Step 2에서 표시). */
export const useRollForwardInfo = (workbookId: string | undefined) =>
  useQuery({
    ...queries.workbook(workbookId ?? "")._ctx.rollForwardInfo(),
    enabled: !!workbookId,
  });
